import { Quaternion } from './vector.js';

const tmpQuat1 = new Quaternion();
const tmpQuat2 = new Quaternion();
const tmpQuat3 = new Quaternion();
const strideMap = {
    number: 1,
    vec2: 2,
    vec3: 3,
    vec4: 4,
    quat: 4
};
function numberClamp(x, min, max) {
    return x < min ? min : x > max ? max : x;
}
/**
 * The interpolator class
 * @public
 */ class Interpolator {
    /** @internal */ _prevKey;
    /** @internal */ _prevT;
    /** @internal */ _inputs;
    /** @internal */ _outputs;
    /** @internal */ _mode;
    /** @internal */ _target;
    /** @internal */ _stride;
    /** @internal */ _maxTime;
    /** @internal */ _a;
    /** @internal */ _h;
    /**
   * Interpolation target to stride
   * @param target - The interpolation target
   * @returns Stride of the target
   */ static getTargetStride(target) {
        return strideMap[target] ?? 0;
    }
    /**
   * Creates a interpolator instance
   * @param mode - The interpolation mode
   * @param target - The interpolation target
   * @param inputs - Linear time in seconds
   * @param outputs - Vector or scalars representing the properties to be interpolated
   * @param stride - Stride of outputs
   */ constructor(mode, target, inputs, outputs){
        this._prevKey = 0;
        this._prevT = 0;
        this._inputs = inputs;
        this._outputs = outputs;
        this._mode = mode;
        this._target = target;
        this._stride = target ? strideMap[target] : Math.floor(outputs.length / inputs.length);
        this._maxTime = inputs[inputs.length - 1];
        this._a = null;
        this._h = null;
    }
    clone() {
        const inputs = this._inputs.slice();
        const outputs = this._outputs.slice();
        return new Interpolator(this._mode, this._target, inputs, outputs);
    }
    /** Gets the interpolation mode */ get mode() {
        return this._mode;
    }
    set mode(val) {
        if (val !== this._mode) {
            this._mode = val;
            this._stride = this._target ? strideMap[this._target] : Math.floor(this._outputs.length / this._inputs.length);
        }
    }
    /** Gets the interpolation target */ get target() {
        return this._target;
    }
    set target(val) {
        if (val !== this._target) {
            this._target = val;
            this._stride = this._target ? strideMap[this._target] : Math.floor(this._outputs.length / this._inputs.length);
        }
    }
    /** stride */ get stride() {
        return this._stride;
    }
    get maxTime() {
        return this._maxTime;
    }
    /** inputs */ get inputs() {
        return this._inputs;
    }
    set inputs(val) {
        if (val !== this._inputs) {
            this._inputs = val;
            this._a = null;
            this._h = null;
        }
    }
    /** outputs */ get outputs() {
        return this._outputs;
    }
    set outputs(val) {
        if (val !== this._outputs) {
            this._outputs = val;
            this._a = null;
            this._h = null;
        }
    }
    /**
   * Calculates the interpolated value at a given time
   * @param t - The time to calcuate interpolation
   * @param maxTime - The maxmium time duration
   * @param result - The calculated interpolation value
   * @returns The calcuated interpolation value
   */ interpolate(t, result) {
        const ot = t;
        const input = this._inputs;
        const output = this._outputs;
        if (output.length === this._stride) {
            for(let i = 0; i < this._stride; i++){
                result[i] = output[i];
            }
            return result;
        }
        t = numberClamp(t, input[0], input[input.length - 1]);
        if (this._prevT > t) {
            this._prevKey = 0;
        }
        this._prevT = t;
        let nextKey = 0;
        for(let i = this._prevKey; i < input.length; ++i){
            if (t <= input[i]) {
                nextKey = numberClamp(i, 1, input.length - 1);
                break;
            }
        }
        this._prevKey = numberClamp(nextKey - 1, 0, nextKey);
        const keyDelta = input[nextKey] - input[this._prevKey];
        const tn = (t - input[this._prevKey]) / keyDelta;
        if (this._target === 'quat') {
            if (this._mode === 'cubicspline') {
                this.cubicSpline(this._prevKey, nextKey, keyDelta, tn, result);
                return result;
            } else if (this._mode === 'cubicspline-natural') {
                this.cubicSplineNatural(this._prevKey, nextKey, ot, tn, result);
            } else if (this._mode === 'linear') {
                this.getQuat(this._prevKey, tmpQuat1);
                this.getQuat(nextKey, tmpQuat2);
                this.slerpQuat(tmpQuat1, tmpQuat2, tn, tmpQuat3);
                result[0] = tmpQuat3.x;
                result[1] = tmpQuat3.y;
                result[2] = tmpQuat3.z;
                result[3] = tmpQuat3.w;
                return result;
            } else {
                return this.getQuat(this._prevKey, result);
            }
        }
        switch(this._mode){
            case 'step':
                return this.step(this._prevKey, result);
            case 'cubicspline':
                return this.cubicSpline(this._prevKey, nextKey, keyDelta, tn, result);
            case 'cubicspline-natural':
                return this.cubicSplineNatural(this._prevKey, nextKey, ot, tn, result);
            case 'linear':
            default:
                return this.linear(this._prevKey, nextKey, tn, result);
        }
    }
    /** @internal */ getQuat(index, result) {
        result[0] = this._outputs[4 * index];
        result[1] = this._outputs[4 * index + 1];
        result[2] = this._outputs[4 * index + 2];
        result[3] = this._outputs[4 * index + 3];
        return result;
    }
    /** @internal */ step(prevKey, result) {
        for(let i = 0; i < this._stride; i++){
            result[i] = this._outputs[prevKey * this._stride + i];
        }
        return result;
    }
    /** @internal */ linear(prevKey, nextKey, t, result) {
        for(let i = 0; i < this._stride; i++){
            result[i] = this._outputs[prevKey * this._stride + i] * (1 - t) + this._outputs[nextKey * this._stride + i] * t;
        }
        return result;
    }
    /** @internal */ cubicSpline(prevKey, nextKey, keyDelta, t, result) {
        const prevIndex = prevKey * this._stride * 3;
        const nextIndex = nextKey * this._stride * 3;
        const A = 0;
        const V = this._stride;
        const B = 2 * this._stride;
        const tSq = t * t;
        const tCub = tSq * t;
        for(let i = 0; i < this._stride; i++){
            const v0 = this._outputs[prevIndex + i + V];
            const a = keyDelta * this._outputs[nextIndex + i + A];
            const b = keyDelta * this._outputs[prevIndex + i + B];
            const v1 = this._outputs[nextIndex + i + V];
            result[i] = (2 * tCub - 3 * tSq + 1) * v0 + (tCub - 2 * tSq + t) * b + (-2 * tCub + 3 * tSq) * v1 + (tCub - tSq) * a;
        }
        return result;
    }
    /** @internal */ cubicSplineNatural(prevKey, nextKey, t, tn, result) {
        if (this._inputs.length === 2) {
            return this.linear(prevKey, nextKey, tn, result);
        }
        if (!this._a) {
            this._prepareCubicSplineNatural();
        }
        const seg = Math.min(Math.max(this._getSegment(t), 0), this._inputs.length - 2) + 1;
        const t1 = t - this._inputs[seg - 1];
        const t2 = this._h[seg] - t1;
        for(let i = 0; i < this._stride; i++){
            result[i] = ((-this._a[(seg - 1) * this._stride + i] / 6 * (t2 + this._h[seg]) * t1 + this._outputs[(seg - 1) * this._stride + i]) * t2 + (-this._a[seg * this._stride + i] / 6 * (t1 + this._h[seg]) * t2 + this._outputs[seg * this._stride + i]) * t1) / this._h[seg];
        }
        return result;
    }
    _prepareCubicSplineNatural() {
        const nk = this._inputs.length;
        const sub = new Array(nk - 1);
        const diag = new Array(nk - 1);
        const sup = new Array(nk - 1);
        this._h = new Array(nk);
        this._a = new Array(nk * this._stride);
        for(let i = 0; i < this._stride; i++){
            this._a[i] = 0;
            this._a[nk * this._stride - 1 - i] = 0;
        }
        for(let i = 1; i < nk; ++i){
            this._h[i] = this._inputs[i] - this._inputs[i - 1];
        }
        for(let i = 1; i < nk - 1; ++i){
            diag[i] = (this._h[i] + this._h[i + 1]) / 3;
            sup[i] = this._h[i + 1] / 6.0;
            sub[i] = this._h[i] / 6;
            for(let j = 0; j < this._stride; j++){
                const k = i * this._stride + j;
                this._a[k] = (this._outputs[k + this._stride] - this._outputs[k]) / this._h[i + 1] - (this._outputs[k] - this._outputs[k - this._stride]) / this._h[i];
            }
        }
        this.solveTridiag(sub, diag, sup);
    }
    solveTridiag(sub, diag, sup) {
        const n = this._inputs.length - 2;
        for(let i = 2; i <= n; ++i){
            sub[i] /= diag[i - 1];
            diag[i] -= sub[i] * sup[i - 1];
            this._a[i] -= this._a[i - 1] * sub[i];
        }
        for(let i = 0; i < this._stride; i++){
            this._a[n * this._stride + i] /= diag[n];
        }
        for(let i = n - 1; i >= 1; --i){
            for(let j = 0; j < this._stride; j++){
                const k = i * this._stride + j;
                this._a[k] = (this._a[k] - this._a[k + this._stride] * sup[i]) / diag[i];
            }
        }
    }
    _getSegment(x) {
        if (x < this._inputs[0]) {
            return -1;
        }
        if (x >= this._inputs[this._inputs.length - 1]) {
            return this._inputs.length;
        }
        let result = 0;
        for(; result < this._inputs.length - 1; ++result){
            if (x < this._inputs[result + 1]) {
                break;
            }
        }
        if (result == this._inputs.length) {
            result = this._inputs.length - 1;
        }
        return result;
    }
    /** @internal */ slerpQuat(q1, q2, t, result) {
        return Quaternion.slerp(q1, q2, t, result).inplaceNormalize();
    }
}
const tmpArray = new Float32Array(1);
/**
 * A helper class for scalar interpolation
 * @public
 */ class InterpolatorScalar extends Interpolator {
    constructor(mode, inputs, outputs){
        super(mode, 'number', inputs, outputs);
    }
    clone() {
        const inputs = this._inputs.slice();
        const outputs = this._outputs.slice();
        return new InterpolatorScalar(this._mode, inputs, outputs);
    }
    static constant(value) {
        return new InterpolatorScalar('step', new Float32Array([
            0
        ]), new Float32Array([
            value
        ]));
    }
    static linear(value0, value1, t0 = 0, t1 = 1) {
        return new InterpolatorScalar('linear', new Float32Array([
            t0,
            t1
        ]), new Float32Array([
            value0,
            value1
        ]));
    }
    evaluate(t) {
        this.interpolate(t, tmpArray);
        return tmpArray[0];
    }
}

export { Interpolator, InterpolatorScalar };
//# sourceMappingURL=interpolator.js.map
